<script setup>
import AccountInformationItem from "./AccountInformationItem.vue";
import {
  userData,
  userDataLoading,
  getWorkflowStatusFromName,
  useRouter,
  supportingFormDataLoading,
  fetchUserDataOnMount,
} from "@/utils/vue_helpers";
import { getReceiptsFromBills } from "@/utils/functions";
import { routes } from "@/router/routes";
import { computed, watchEffect, reactive } from "vue";
import { useClientDashboardCounts } from "./account_dashboard_business_logic";

fetchUserDataOnMount();

const { router } = useRouter();
const { numberOfBuildings, numberOfSchemePlans, countsLoading } =
  useClientDashboardCounts();

const accountInfoData = reactive({
  numberOfProperties: null,
  numberOfBusinesses: null,
  numberOfReceipts: null,
  numberOfBills: null,
  numberOfUnpaidBills: null,
  numberOfPaidBills: null,
  numberOfVehicles: null,
  numberOfHouses: null,
});

watchEffect(() => {
  const bills = userData.value?.bills ?? null;
  accountInfoData.numberOfProperties = userData.value?.properties?.length ?? 0;
  accountInfoData.numberOfBusinesses = userData.value?.businesses?.length ?? 0;
  accountInfoData.numberOfReceipts = getReceiptsFromBills(bills)?.length ?? 0;
  accountInfoData.numberOfBills = bills?.length ?? 0;
  accountInfoData.numberOfVehicles = userData.value?.vehicles?.length ?? 0;
  accountInfoData.numberOfHouses = userData.value?.houses?.length ?? 0;
  accountInfoData.numberOfUnpaidBills = 0;
  accountInfoData.numberOfPaidBills = 0;
  if (!supportingFormDataLoading.value) {
    const paidWfStatus = getWorkflowStatusFromName("Paid");
    const unpaidWfStatus = getWorkflowStatusFromName("Unpaid");
    bills?.forEach((bill) => {
      const billIsPaid = bill.workflow_status_id === paidWfStatus.id;
      const billIsUnpaid = bill.workflow_status_id === unpaidWfStatus.id;
      if (billIsPaid) {
        accountInfoData.numberOfPaidBills += 1;
      } else if (billIsUnpaid) {
        accountInfoData.numberOfUnpaidBills += 1;
      }
    });
  }
});

const bannerSubtitle = computed(() => {
  if (userDataLoading.value || supportingFormDataLoading.value) {
    return "Loading your account summary…";
  }
  const unpaid = accountInfoData.numberOfUnpaidBills;
  if (unpaid > 0) {
    return `You have ${unpaid} unpaid bill${unpaid === 1 ? "" : "s"}`;
  }
  return "Properties, bills, and county services in one place.";
});

const showUnpaidBannerChip = computed(() => {
  return (
    !userDataLoading.value &&
    !supportingFormDataLoading.value &&
    accountInfoData.numberOfUnpaidBills > 0
  );
});

const propertyStats = computed(() => [
  {
    text: "My Plots",
    icon: "mdi-home-city",
    number: accountInfoData.numberOfProperties,
    accentColor: "blue darken-1",
    route: routes.viewProperties,
  },
  {
    text: "My Businesses",
    icon: "mdi-briefcase",
    number: accountInfoData.numberOfBusinesses,
    accentColor: "orange darken-2",
    route: routes.viewBusinesses,
  },
  {
    text: "My Houses",
    icon: "mdi-home",
    number: accountInfoData.numberOfHouses,
    accentColor: "indigo darken-1",
    route: routes.viewHouses,
  },
  {
    text: "My Vehicles",
    icon: "mdi-car-back",
    number: accountInfoData.numberOfVehicles,
    accentColor: "green darken-1",
    route: routes.viewVehicles,
  },
  {
    text: "My Buildings",
    icon: "mdi-office-building",
    number: numberOfBuildings.value ?? 0,
    accentColor: "deep-purple darken-1",
    route: routes.viewClientBuildings,
    loading: countsLoading.value,
  },
  {
    text: "Scheme Plans",
    icon: "mdi-map-marker-multiple",
    number: numberOfSchemePlans.value ?? 0,
    accentColor: "teal darken-1",
    route: routes.viewSchemePlanProperties,
    loading: countsLoading.value,
  },
]);

function onPaidBillsClicked() {
  router.value.push(
    routes.viewBillsWithQuery({
      tab: "paid",
    })
  );
}

function onUnpaidBillsClicked() {
  router.value.push(
    routes.viewBillsWithQuery({
      tab: "unpaid",
    })
  );
}
</script>

<template>
  <v-container fluid class="px-2 px-sm-3 px-md-4 py-3 py-md-4">
    <v-sheet
      color="primary"
      dark
      rounded="lg"
      class="pa-4 pa-md-5 mb-5"
      elevation="2"
    >
      <div class="d-flex align-center flex-wrap" :style="{ gap: '12px' }">
        <v-icon size="36" class="flex-shrink-0">mdi-view-dashboard</v-icon>
        <div class="flex-grow-1" :style="{ minWidth: '200px' }">
          <div class="text-overline text-uppercase mb-1" :style="{ opacity: 0.85 }">
            County client portal
          </div>
          <div class="text-h6 text-md-h5 font-weight-bold">
            My Dashboard
          </div>
          <div class="text-body-2 mt-1" :style="{ opacity: 0.9 }">
            {{ bannerSubtitle }}
          </div>
        </div>
        <v-chip
          v-if="showUnpaidBannerChip"
          small
          color="error"
          class="font-weight-medium dashboard-banner-chip"
          @click="onUnpaidBillsClicked"
        >
          <v-icon left small>mdi-alert-circle-outline</v-icon>
          {{ accountInfoData.numberOfUnpaidBills }} unpaid
        </v-chip>
      </div>
    </v-sheet>

    <div class="mb-2">
      <div class="text-subtitle-1 font-weight-bold primary--text mb-1">
        Property &amp; assets
      </div>
      <div class="text-caption grey--text text--darken-1 mb-3">
        Plots, businesses, buildings, and other registered items
      </div>
    </div>

    <v-row class="stat-grid">
      <v-col
        v-for="(item, index) in propertyStats"
        :key="`property-${index}`"
        cols="12"
        sm="6"
        md="4"
        lg="3"
      >
        <AccountInformationItem
          :text="item.text"
          :icon="item.icon"
          :number="item.number"
          :accent-color="item.accentColor"
          :route="item.route"
          :loading="item.loading"
        />
      </v-col>
    </v-row>

    <div class="mt-6 mb-2">
      <div class="text-subtitle-1 font-weight-bold primary--text mb-1">
        Billing &amp; records
      </div>
      <div class="text-caption grey--text text--darken-1 mb-3">
        Bills, receipts, and payment status
      </div>
    </div>

    <v-row class="stat-grid">
      <v-col cols="12" sm="6" md="4" lg="3">
        <AccountInformationItem
          text="My Bills"
          icon="mdi-bank"
          :number="accountInfoData.numberOfBills"
          accent-color="red darken-2"
          :route="routes.viewBills"
        >
          <template #additionalContent>
            <v-container fluid class="pa-0" @click.stop="">
              <template v-if="supportingFormDataLoading || userDataLoading">
                <v-skeleton-loader
                  width="100%"
                  height="20"
                  type="text"
                  class="mb-2"
                />
                <v-skeleton-loader width="100%" height="20" type="text" />
              </template>
              <template v-else>
                <div class="d-flex flex-wrap" :style="{ gap: '6px' }">
                  <v-chip
                    x-small
                    label
                    color="success"
                    class="font-weight-medium white--text text-none"
                    @click.stop.prevent="onPaidBillsClicked"
                  >
                    Paid · {{ accountInfoData.numberOfPaidBills }}
                  </v-chip>
                  <v-chip
                    x-small
                    label
                    color="error"
                    class="font-weight-medium white--text text-none"
                    @click.stop.prevent="onUnpaidBillsClicked"
                  >
                    Unpaid · {{ accountInfoData.numberOfUnpaidBills }}
                  </v-chip>
                </div>
              </template>
            </v-container>
          </template>
        </AccountInformationItem>
      </v-col>

      <v-col cols="12" sm="6" md="4" lg="3">
        <AccountInformationItem
          text="My Receipts"
          icon="mdi-receipt-text"
          :number="accountInfoData.numberOfReceipts"
          accent-color="blue-grey darken-1"
          :route="routes.viewBillsWithQuery({ tab: 'paid' })"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
.stat-grid >>> .col {
  padding-top: 10px;
  padding-bottom: 10px;
}

.dashboard-banner-chip {
  cursor: pointer;
}
</style>
